import React, {useCallback} from 'react';
import {Button, ButtonStyled} from '@tickchak/react-share/app';
import type {SuggestionIcon} from '../../constants';

/** Already in the user's language — server items carry text, client extras were translated. */
export type AiAgentSuggestionItem = {
    /** Stable across renders and languages: the intent, or intent:labelKey for a client extra. */
    id: string;
    label: string;
    prompt: string;
    /** Shown above the prompt box once tapped; null when the intent has no explanation. */
    description: string | null;
    Icon: SuggestionIcon;
    catalogIndex: number;
    /** Opens the same file picker as the prompt attach button. */
    opensFilePicker?: boolean;
};

interface AiAgentSuggestionChipProps {
    suggestion: AiAgentSuggestionItem;
    /** The file dialog was asked for and has not shown up yet. */
    loading: boolean;
    onSelect: (suggestion: AiAgentSuggestionItem) => void;
}

function AiAgentSuggestionChip({suggestion, loading, onSelect}: AiAgentSuggestionChipProps) {
    const {Icon, label, catalogIndex} = suggestion;

    const handleClick = useCallback(() => onSelect(suggestion), [onSelect, suggestion]);

    return (
        <Button
            variant="neutral"
            size="category"
            // The library's own spinner, in the icon slot rather than via `xhrState` — that
            // hides the label too, and the chip would collapse and reflow the row.
            iconStart={loading
                ? <ButtonStyled.LoadingSpinner data-cy={`account-ai-agent-suggestion-loader-${catalogIndex}`} />
                : <Icon width={20} height={20} stroke="currentColor" />}
            disabled={loading}
            onClick={handleClick}
            data-cy={`account-ai-agent-suggestion-${catalogIndex}`}
            customStyle={{borderColor: 'var(--color-border-subtle)'}}
        >
            {label}
        </Button>
    );
}

export default AiAgentSuggestionChip;
